import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useColorScheme, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeColors {
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  primary: string;
  accent: string;
  error: string;
  tabBar: string;
  tabBarBorder: string;
}

interface ThemeContextType {
  themeMode: ThemeMode;
  isDark: boolean;
  colors: ThemeColors;
  setThemeMode: (mode: ThemeMode) => Promise<void>;
}

const lightColors: ThemeColors = {
  background: '#F5F5F5', // Light gray instead of white
  surface: '#E5E5E5', // Slightly darker gray for surfaces
  text: '#000000',
  textSecondary: '#737373',
  border: '#E4E4E4',
  primary: '#67b73dff', // Hyzone green
  accent: '#FF4500',
  error: '#FF4500',
  tabBar: '#F5F5F5', // Light gray instead of white
  tabBarBorder: '#E4E4E4',
};

const darkColors: ThemeColors = {
  background: '#121212', // Dark gray instead of pure black
  surface: '#1A1A1A',
  text: '#FFFFFF',
  textSecondary: '#A0A0A0',
  border: '#2A2A2A',
  primary: '#67b73dff', // Hyzone green
  accent: '#FF6B35',
  error: '#FF6B35',
  tabBar: '#0F0F0F', // Dark gray instead of pure black
  tabBarBorder: '#2A2A2A',
};

const THEME_STORAGE_KEY = '@hyzone_theme';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const systemColorScheme = useColorScheme();
  const [themeMode, setThemeModeState] = useState<ThemeMode>('system');
  const [isLoaded, setIsLoaded] = useState(false);

  // Determine if dark mode should be active
  const isDark =
    themeMode === 'dark' || (themeMode === 'system' && systemColorScheme === 'dark');

  const colors = isDark ? darkColors : lightColors;

  // Load saved theme preference
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const savedTheme = await AsyncStorage.getItem(THEME_STORAGE_KEY);
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
          setThemeModeState(savedTheme as ThemeMode);
        }
      } catch (error) {
        console.error('Failed to load theme preference:', error);
      } finally {
        setIsLoaded(true);
      }
    };
    loadTheme();
  }, []);

  const setThemeMode = async (mode: ThemeMode) => {
    try {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, mode);
      setThemeModeState(mode);
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  // Render immediately on web, wait for load on native to prevent flash
  // On web, AsyncStorage operations shouldn't block initial render
  const shouldWait = Platform.OS !== 'web' && !isLoaded;

  if (shouldWait) {
    return null;
  }

  return (
    <ThemeContext.Provider value={{ themeMode, isDark, colors, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
